import { useCallback, useEffect, useState } from "react";
import { fetchTypingLayout } from "../../api";

// One layout fetch per session, shared by every remapped field.
let layoutPromise = null;
function getLayoutKeys() {
  if (!layoutPromise) {
    layoutPromise = fetchTypingLayout()
      .then((d) => d.keys || null)
      .catch(() => null);
  }
  return layoutPromise;
}

const REMAP_STORAGE_KEY = "typing.remap"; // shared with the typing tutor

/**
 * useArabicFieldRemap — the typing tutor's physical-key → Arabic-101 remap,
 * adapted for real <input>/<textarea> fields (the tutor's own hook listens on
 * window and deliberately bypasses inputs — right for drills, wrong here).
 *
 * With remap on (the default — almost nobody has an Arabic OS layout), a
 * learner on a plain US keyboard presses the same physical keys the typing
 * lessons taught and gets the same Arabic characters, ḥarakāt via Shift
 * included. Modifier combos, Backspace, Enter, arrows and any unmapped key
 * pass through untouched, so shortcuts and navigation keep working.
 *
 * Shares the tutor's `typing.remap` localStorage preference: turning remap
 * off in either place turns it off in both.
 *
 * Usage:
 *   const { remapOn, toggleRemap, remapKeyDown } = useArabicFieldRemap();
 *   <textarea onKeyDown={(e) => remapKeyDown(e, setValue)} … />
 */
export default function useArabicFieldRemap() {
  const [keys, setKeys] = useState(null);
  const [remapOn, setRemapOn] = useState(() => {
    try {
      return localStorage.getItem(REMAP_STORAGE_KEY) !== "false";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    let cancelled = false;
    getLayoutKeys().then((k) => !cancelled && setKeys(k));
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleRemap = useCallback(() => {
    setRemapOn((v) => {
      try {
        localStorage.setItem(REMAP_STORAGE_KEY, String(!v));
      } catch {
        /* private browsing — the preference just won't persist */
      }
      return !v;
    });
  }, []);

  const remapKeyDown = useCallback(
    (event, setValue) => {
      if (!remapOn || !keys) return;
      // Never swallow browser/OS shortcuts.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const key = keys[event.code];
      if (!key) return; // Backspace, Enter, arrows, digits… pass through
      const char = event.shiftKey ? key.shift : key.base;
      if (!char) return;

      event.preventDefault();
      const el = event.target;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? start;
      setValue(el.value.slice(0, start) + char + el.value.slice(end));
      // React's re-render puts the caret at the end; put it back after the
      // inserted character (mid-text edits stay where the user is typing).
      const pos = start + char.length;
      requestAnimationFrame(() => {
        try {
          el.setSelectionRange(pos, pos);
        } catch {
          /* element unmounted */
        }
      });
    },
    [remapOn, keys]
  );

  return { remapOn, toggleRemap, remapKeyDown, ready: !!keys };
}
